/**
 * Cor do fundo da mídia (config `imageFillColor` do item, do documento e da empresa): a sobra
 * em volta da foto e da miniatura do vídeo quando saem inteiras ("Inteira, sem cortes").
 * Espelho do `MediaFillStyle` do PdfEngine e do `mediaFillStyle` do pdf-renderer: aceita
 * #rrggbb e `transparent` (sem fundo nenhum); fora disso vale o padrão. `null` no update-config do item volta a seguir o documento.
 */

/** Padrão: o cinza claro do editor (--backgroundSecondary do tema claro), o mesmo do PDF. */
export const MEDIA_FILL_DEFAULT = '#f3f4f6';

/** Sem fundo: aparece o fundo da folha (no PDF) ou do cartão (no editor). */
export const MEDIA_FILL_TRANSPARENT = 'transparent';

export interface MediaFillChoice {
    hex: string;
    labelKey: string;
}

/**
 * Paleta do painel: transparente primeiro (pedido do usuário), depois o padrão, branco, preto
 * (plantas, prints e fotos escuras) e os tons claros das famílias do destaque, que conversam
 * com o resto do documento.
 */
export const MEDIA_FILL_CHOICES: readonly MediaFillChoice[] = [
    { hex: MEDIA_FILL_TRANSPARENT, labelKey: 'document.edit.mediaFill.transparent' },
    { hex: MEDIA_FILL_DEFAULT, labelKey: 'document.edit.mediaFill.lightGray' },
    { hex: '#ffffff', labelKey: 'document.edit.mediaFill.white' },
    { hex: '#141515', labelKey: 'document.edit.mediaFill.black' },
    { hex: '#eaf2ff', labelKey: 'document.edit.mediaFill.blue' },
    { hex: '#ecf9f1', labelKey: 'document.edit.mediaFill.green' },
    { hex: '#fff5e8', labelKey: 'document.edit.mediaFill.sand' },
    { hex: '#f1edfd', labelKey: 'document.edit.mediaFill.lilac' },
];

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Cor válida (ou `transparent`) em minúsculas, ou null (segue o nível de cima, ou o padrão). */
export function parseMediaFill(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const color = raw.trim().toLowerCase();
    return HEX_COLOR.test(color) || color === MEDIA_FILL_TRANSPARENT ? color : null;
}

/** A cor é um #rrggbb (o seletor livre só entende esse formato). */
export function isHexMediaFill(color: string): boolean {
    return HEX_COLOR.test(color);
}

interface MediaFillLevel {
    imageFillColor?: string | null;
}

/**
 * Cor efetiva: o primeiro nível que define (item, documento, empresa), como o `??` do PDF;
 * fora do formato, o padrão.
 */
export function resolveMediaFill(levels: (MediaFillLevel | null | undefined)[]): string {
    for (const level of levels) {
        const raw = level?.imageFillColor;
        if (raw !== undefined && raw !== null) return parseMediaFill(raw) ?? MEDIA_FILL_DEFAULT;
    }
    return MEDIA_FILL_DEFAULT;
}

/** A cor não é uma das prontas (o seletor livre acende). */
export function isCustomMediaFill(color: string): boolean {
    return !MEDIA_FILL_CHOICES.some(choice => choice.hex === color);
}
